/*
 * Purpose: Primary activities + builder nav groups (SSOT for IA)
 * Last modified: 2026-06-26 | Agent
 * Completeness: 95
 */
import { PRODUCT_TERMS } from '@/lib/productTerms';

/** Steady-state modes after first use */
export type ProductActivityId =
  | 'my-autoblocks'
  | 'map'
  | 'operate'
  | 'report'
  | 'integrations'
  | 'settings';

export type BuilderPanelId =
  | 'flows'
  | 'templates'
  | 'builder'
  | 'scheduler'
  | 'monitor'
  | 'debug'
  | 'analytics'
  | 'logs'
  | 'activity'
  | 'stats'
  | 'secrets'
  | 'connections'
  | 'integrations'
  | 'settings'
  | 'versions'
  | 'collaborate';

export interface BuilderNavItem {
  id: BuilderPanelId;
  label: string;
  description: string;
}

export interface BuilderNavGroup {
  activityId: ProductActivityId;
  label: string;
  description: string;
  items: BuilderNavItem[];
}

/** Grouped nav — not a flat wide tab strip */
export const BUILDER_NAV_GROUPS: BuilderNavGroup[] = [
  {
    activityId: 'my-autoblocks',
    label: 'My AutoBlocks',
    description: 'Your flows, folders, and starting points',
    items: [
      {
        id: 'flows',
        label: 'My flows',
        description: 'Open, rename, duplicate flows you own',
      },
      {
        id: 'templates',
        label: 'Flow Templates',
        description: 'Clone production-ready starter flows into your workspace',
      },
    ],
  },
  {
    activityId: 'map',
    label: PRODUCT_TERMS.mission,
    description: 'Edit blocks and wiring for the selected flow',
    items: [
      {
        id: 'builder',
        label: PRODUCT_TERMS.mission,
        description: 'Canvas, check, start, stop — flow-scoped controls',
      },
    ],
  },
  {
    activityId: 'operate',
    label: 'Operate',
    description: 'Run schedules and live health for flows',
    items: [
      { id: 'scheduler', label: 'Scheduler', description: 'When this flow runs' },
      { id: 'monitor', label: 'Monitor', description: 'Live execution status' },
      { id: 'debug', label: 'Debug', description: 'Errors and emergency diagnosis' },
    ],
  },
  {
    activityId: 'report',
    label: 'Usage & reporting',
    description: 'History and metrics across flows',
    items: [
      { id: 'analytics', label: 'Analytics', description: 'Trends and performance' },
      { id: 'logs', label: 'Logs', description: 'Execution log for current flow' },
      { id: 'activity', label: 'Activity', description: 'Recent events' },
      { id: 'stats', label: 'Stats', description: 'Summary counters' },
    ],
  },
  {
    activityId: 'integrations',
    label: 'Integrations',
    description: 'Vault, connectors, third-party apps',
    items: [
      { id: 'secrets', label: 'Secrets vault', description: 'API keys and credentials' },
      { id: 'connections', label: 'Connector library', description: 'Available blocks & connectors' },
      { id: 'integrations', label: 'External apps', description: 'Linked services' },
    ],
  },
  {
    activityId: 'settings',
    label: 'Settings',
    description: 'Flow prefs, versions, sharing',
    items: [
      { id: 'settings', label: 'Flow settings', description: 'This flow’s configuration' },
      { id: 'versions', label: 'Versions', description: 'History and rollback' },
      { id: 'collaborate', label: 'Share', description: 'Collaborators and links' },
    ],
  },
];

export const DEFAULT_BUILDER_PANEL: BuilderPanelId = 'builder';

const BUILDER_PANEL_SET = new Set<string>(
  BUILDER_NAV_GROUPS.flatMap((g) => g.items.map((i) => i.id))
);

export function isBuilderPanelId(value: string | null): value is BuilderPanelId {
  return value !== null && BUILDER_PANEL_SET.has(value);
}

export function findNavGroup(activityId: ProductActivityId): BuilderNavGroup | undefined {
  return BUILDER_NAV_GROUPS.find((g) => g.activityId === activityId);
}

export function panelActivity(panelId: BuilderPanelId): ProductActivityId {
  for (const group of BUILDER_NAV_GROUPS) {
    if (group.items.some((i) => i.id === panelId)) return group.activityId;
  }
  return 'map';
}

export function findBuilderNavItem(panelId: BuilderPanelId): BuilderNavItem | undefined {
  for (const group of BUILDER_NAV_GROUPS) {
    const item = group.items.find((i) => i.id === panelId);
    if (item) return item;
  }
  return undefined;
}
